import { useEffect, useState, useRef } from "react";
import { api, imgUrl } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ExternalLink, Loader2, Upload, Lock, Link2, Copy, Ban, Star } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const empty = { name: "", handle: "", tagline: "", accent_color: "#0095f6", logo_url: "", banner_url: "", published: true, featured: false, category: "", password_protected: false, access_password: "", access_whitelist_ips: [], seo_title: "", seo_description: "", socials: {} };
const SOCIAL_DEFS = [
  { key: "instagram", label: "Instagram", ph: "https://instagram.com/yourbrand" },
  { key: "tiktok", label: "TikTok", ph: "https://tiktok.com/@yourbrand" },
  { key: "facebook", label: "Facebook", ph: "https://facebook.com/yourbrand" },
  { key: "youtube", label: "YouTube", ph: "https://youtube.com/@yourbrand" },
  { key: "twitter", label: "X / Twitter", ph: "https://x.com/yourbrand" },
  { key: "website", label: "Website", ph: "https://yourbrand.com" },
  { key: "email", label: "Email", ph: "hello@yourbrand.com" },
];
const ACCENTS = ["#0095f6", "#dd2a7b", "#8134af", "#f58529", "#515bd4", "#10b981", "#000000"];
const CATEGORIES = ["Fashion", "Sneakers", "Beauty", "Home", "Tech", "Accessories", "Food & Drink", "Art & Design", "Other"];

export default function StoresPage() {
  const [stores, setStores] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const bannerRef = useRef();
  const [linksStore, setLinksStore] = useState(null);
  const [tokens, setTokens] = useState([]);
  const [tokensLoading, setTokensLoading] = useState(false);
  const [creatingToken, setCreatingToken] = useState(false);
  const [newLabel, setNewLabel] = useState("");

  const loadTokens = async (storeId) => {
    setTokensLoading(true);
    try { const r = await api.get(`/stores/${storeId}/access-tokens`); setTokens(r.data); }
    finally { setTokensLoading(false); }
  };
  const openLinks = (s) => { setLinksStore(s); setTokens([]); setNewLabel(""); loadTokens(s.id); };
  const createToken = async () => {
    setCreatingToken(true);
    try {
      await api.post(`/stores/${linksStore.id}/access-tokens`, { label: newLabel });
      setNewLabel("");
      await loadTokens(linksStore.id);
      toast.success("Access link created");
    } catch { toast.error("Could not create link"); }
    finally { setCreatingToken(false); }
  };
  const revokeToken = async (id) => {
    await api.post(`/access-tokens/${id}/revoke`);
    await loadTokens(linksStore.id);
    toast.success("Link revoked");
  };
  const deleteToken = async (id) => {
    await api.delete(`/access-tokens/${id}`);
    await loadTokens(linksStore.id);
  };
  const tokenLink = (t) => `${window.location.origin}/${linksStore.handle}?token=${t.token}`;

  const load = () => api.get("/stores").then((r) => setStores(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditing(null); setOpen(true); };
  const openEdit = (s) => { setForm({ ...s }); setEditing(s.id); setOpen(true); };

  const upload = async (file, field = "logo_url") => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, [field]: res.data.url }));
      if (res.data.optimized) {
        const kb = (n) => `${Math.round(n / 1024)} KB`;
        toast.success(`Image optimized → WebP (${kb(res.data.original_size)} → ${kb(res.data.size)})`);
      }
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name || !form.handle) { toast.error("Name and handle are required"); return; }
    if (form.password_protected && !form.access_password?.trim()) { toast.error("Set a password or turn off Members only"); return; }
    setSaving(true);
    try {
      if (editing) await api.put(`/stores/${editing}`, form);
      else await api.post("/stores", form);
      toast.success("Store saved");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this store and all its products?")) return;
    await api.delete(`/stores/${id}`);
    toast.success("Store deleted");
    load();
  };

  return (
    <div className="animate-fade-up" data-testid="stores-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <span className="text-xs font-heading font-bold uppercase tracking-[0.2em] text-zinc-500">Manage</span>
          <h1 className="font-heading font-light text-4xl tracking-tight mt-1">Stores</h1>
        </div>
        <button onClick={openNew} data-testid="new-store-button" className="flex items-center gap-2 bg-black text-white rounded-full px-5 py-3 text-sm font-body font-semibold active:scale-95 transition-transform">
          <Plus size={16} /> New store
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {stores.map((s) => (
          <div key={s.id} className="bg-white border border-zinc-200/80 rounded-xl p-5" data-testid={`store-row-${s.handle}`}>
            <div className="flex items-center gap-3">
              <img src={imgUrl(s.logo_url)} alt="" className="h-12 w-12 rounded-full object-cover bg-zinc-100" />
              <div className="flex-1 min-w-0">
                <div className="font-heading font-semibold truncate flex items-center gap-1.5">
                  {s.name}
                  {s.featured ? <Star size={12} className="fill-amber-400 stroke-amber-400 shrink-0" data-testid={`store-featured-${s.handle}`} /> : null}
                  {s.password_protected && s.access_password ? <Lock size={12} className="text-zinc-400" data-testid={`store-locked-${s.handle}`} /> : null}
                </div>
                <div className="text-xs text-zinc-400">@{s.handle} · {s.product_count} products</div>
              </div>
              <span className="h-3 w-3 rounded-full" style={{ background: s.accent_color }} />
            </div>
            <p className="text-sm text-zinc-500 mt-3 line-clamp-2 font-body">{s.tagline}</p>
            {s.password_protected && s.access_password ? (
              <button
                onClick={() => openLinks(s)}
                data-testid={`manage-links-${s.handle}`}
                className="mt-3 w-full flex items-center justify-center gap-1.5 border border-zinc-200 rounded-lg py-2 text-xs font-semibold hover:bg-zinc-50 text-zinc-600"
              >
                <Link2 size={13} /> Access links
              </button>
            ) : null}
            <div className="flex gap-2 mt-4">
              <a href={`/${s.handle}`} target="_blank" rel="noreferrer" className="flex-1 flex items-center justify-center gap-1 border border-zinc-200 rounded-lg py-2 text-xs font-semibold hover:bg-zinc-50" data-testid={`view-store-${s.handle}`}><ExternalLink size={13} /> View</a>
              <button onClick={() => openEdit(s)} data-testid={`edit-store-${s.handle}`} className="flex-1 flex items-center justify-center gap-1 border border-zinc-200 rounded-lg py-2 text-xs font-semibold hover:bg-zinc-50"><Pencil size={13} /> Edit</button>
              <button onClick={() => remove(s.id)} data-testid={`delete-store-${s.handle}`} className="border border-zinc-200 rounded-lg py-2 px-3 text-xs hover:bg-rose-50 hover:text-rose-500"><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit store" : "New store"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="ig-ring p-[2px] rounded-full">
                <div className="bg-white p-[2px] rounded-full">
                  <img src={form.logo_url ? imgUrl(form.logo_url) : "https://images.unsplash.com/photo-1536180838057-b604200e6f36?w=100&q=80"} alt="" className="h-16 w-16 rounded-full object-cover bg-zinc-100" />
                </div>
              </div>
              <div>
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="store-logo-input" />
                <button onClick={() => fileRef.current.click()} className="flex items-center gap-2 border border-zinc-200 rounded-lg px-3 py-2 text-sm font-semibold hover:bg-zinc-50" data-testid="store-logo-upload">
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload logo
                </button>
              </div>
            </div>
            <Field label="Store name"><input data-testid="store-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" /></Field>
            <Field label="Handle"><input data-testid="store-handle-input" value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value })} placeholder="my-store" className="input" /></Field>
            <Field label="Tagline"><input data-testid="store-tagline-input" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className="input" /></Field>
            <div className="border border-zinc-200 rounded-xl p-4 space-y-3">
              <div className="text-sm font-heading font-semibold">SEO (search engines)</div>
              <Field label="SEO title (optional)">
                <input data-testid="store-seo-title-input" value={form.seo_title || ""} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} placeholder={`${form.name || "Store"} — Shop on StoreIG`} maxLength={70} className="input" />
              </Field>
              <Field label="Meta description (optional)">
                <textarea data-testid="store-seo-desc-input" value={form.seo_description || ""} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} placeholder="Falls back to your tagline. ~150 characters shows best in Google." rows={2} maxLength={200} className="input" />
                <div className="text-[11px] text-zinc-400 mt-1">{(form.seo_description || "").length}/200 · overrides the auto-generated description</div>
              </Field>
            </div>
            <Field label="Hero banner (optional)">
              <div className="flex items-center gap-3">
                {form.banner_url ? (
                  <img src={imgUrl(form.banner_url)} alt="" className="h-14 w-28 rounded-lg object-cover bg-zinc-100" />
                ) : (
                  <div className="h-14 w-28 rounded-lg bg-zinc-100 flex items-center justify-center text-xs text-zinc-400">No banner</div>
                )}
                <input ref={bannerRef} type="file" accept="image/*" hidden onChange={(e) => e.target.files[0] && upload(e.target.files[0], "banner_url")} data-testid="store-banner-input" />
                <button onClick={() => bannerRef.current.click()} className="flex items-center gap-2 border border-zinc-200 rounded-lg px-3 py-2 text-sm font-semibold hover:bg-zinc-50" data-testid="store-banner-upload">
                  <Upload size={14} /> Upload banner
                </button>
              </div>
            </Field>
            <Field label="Accent color">
              <div className="flex gap-2">
                {ACCENTS.map((c) => (
                  <button key={c} onClick={() => setForm({ ...form, accent_color: c })} className={`h-8 w-8 rounded-full ${form.accent_color === c ? "ring-2 ring-offset-2 ring-black" : ""}`} style={{ background: c }} data-testid={`accent-${c}`} />
                ))}
              </div>
            </Field>
            <Field label="Category">
              <select data-testid="store-category-input" value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input">
                <option value="">Uncategorised</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Social links (shown in your store footer)">
              <div className="space-y-2">
                {SOCIAL_DEFS.map((sd) => (
                  <div key={sd.key} className="flex items-center gap-2">
                    <span className="text-xs text-zinc-500 w-24 shrink-0">{sd.label}</span>
                    <input
                      data-testid={`store-social-${sd.key}`}
                      value={form.socials?.[sd.key] || ""}
                      onChange={(e) => setForm({ ...form, socials: { ...(form.socials || {}), [sd.key]: e.target.value } })}
                      placeholder={sd.ph}
                      className="input flex-1"
                    />
                  </div>
                ))}
              </div>
              <div className="text-[11px] text-zinc-400 mt-1">Leave blank to hide. Only these socials are shown to shoppers.</div>
            </Field>
            <div className="flex items-center justify-between border border-zinc-200 rounded-xl p-4">
              <div className="flex items-center gap-2">
                <Star size={15} className="text-zinc-500" />
                <div>
                  <div className="text-sm font-heading font-semibold">Featured store</div>
                  <div className="text-xs text-zinc-400">Pin to the top of the directory with a badge.</div>
                </div>
              </div>
              <button
                type="button" role="switch" aria-checked={!!form.featured}
                onClick={() => setForm({ ...form, featured: !form.featured })}
                data-testid="store-featured-toggle"
                className={`relative h-6 w-11 rounded-full transition-colors shrink-0 ${form.featured ? "bg-black" : "bg-zinc-200"}`}
              >
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${form.featured ? "translate-x-[22px]" : "translate-x-0.5"}`} />
              </button>
            </div>
            <div className="border border-zinc-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Lock size={15} className="text-zinc-500" />
                  <div>
                    <div className="text-sm font-heading font-semibold">Members only</div>
                    <div className="text-xs text-zinc-400">Require a password to view this store.</div>
                  </div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={!!form.password_protected}
                  onClick={() => setForm({ ...form, password_protected: !form.password_protected })}
                  data-testid="store-protect-toggle"
                  className={`relative h-6 w-11 rounded-full transition-colors shrink-0 ${form.password_protected ? "bg-black" : "bg-zinc-200"}`}
                >
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${form.password_protected ? "translate-x-[22px]" : "translate-x-0.5"}`} />
                </button>
              </div>
              {form.password_protected && (
                <input
                  data-testid="store-password-input"
                  value={form.access_password || ""}
                  onChange={(e) => setForm({ ...form, access_password: e.target.value })}
                  placeholder="Store password"
                  className="input mt-3"
                />
              )}
              {form.password_protected && (
                <div className="mt-3">
                  <label className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">Whitelisted IPs (bypass rate-limit)</label>
                  <textarea
                    data-testid="store-whitelist-ips-input"
                    value={(form.access_whitelist_ips || []).join(", ")}
                    onChange={(e) => setForm({ ...form, access_whitelist_ips: e.target.value.split(/[\n,]/).map((s) => s.trim()).filter(Boolean) })}
                    placeholder="Comma or line separated, e.g. 203.0.113.5, 198.51.100.22"
                    rows={2}
                    className="input font-mono text-xs"
                  />
                  <div className="text-[11px] text-zinc-400 mt-1">Trusted IPs skip brute-force protection entirely.</div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <button onClick={save} disabled={saving} data-testid="save-store-button" className="bg-black text-white rounded-full px-6 py-3 text-sm font-semibold active:scale-95 transition-transform disabled:opacity-60 flex items-center gap-2">
              {saving && <Loader2 size={14} className="animate-spin" />} Save store
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <style>{`.input{width:100%;border:1px solid #e4e4e7;border-radius:12px;padding:10px 14px;font-size:14px;outline:none;font-family:'Plus Jakarta Sans'}.input:focus{border-color:#4F46E5}`}</style>

      <Dialog open={!!linksStore} onOpenChange={(o) => !o && setLinksStore(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Link2 size={18} /> Access links — {linksStore?.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-500 -mt-2">Create revocable share links. Anyone with a link unlocks the store — revoke it anytime to kill it without changing the password.</p>
          <div className="flex gap-2 mt-2">
            <input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="Label (e.g. Instagram VIP)" className="input" data-testid="new-token-label" />
            <button onClick={createToken} disabled={creatingToken} data-testid="create-token-button" className="bg-black text-white rounded-full px-5 text-sm font-semibold active:scale-95 transition-transform disabled:opacity-60 flex items-center gap-1.5 whitespace-nowrap">
              {creatingToken ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create link
            </button>
          </div>
          <div className="mt-2 max-h-72 overflow-y-auto space-y-2" data-testid="tokens-list">
            {tokensLoading ? (
              <div className="flex justify-center py-6"><Loader2 className="animate-spin text-zinc-400" size={18} /></div>
            ) : tokens.length === 0 ? (
              <p className="text-sm text-zinc-400 text-center py-6">No links yet. Create one above.</p>
            ) : tokens.map((t) => (
              <div key={t.id} className={`border rounded-xl p-3 ${t.revoked ? "border-zinc-100 bg-zinc-50 opacity-70" : "border-zinc-200"}`} data-testid={`token-${t.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{t.label || "Untitled link"} {t.revoked && <span className="text-rose-500 font-normal">· revoked</span>}</div>
                    <div className="text-xs text-zinc-400">{t.uses} unlock{t.uses === 1 ? "" : "s"}</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {!t.revoked && (
                      <button onClick={() => navigator.clipboard.writeText(tokenLink(t)).then(() => toast.success("Link copied"))} data-testid={`copy-token-${t.id}`} className="p-2 rounded-lg hover:bg-zinc-100 text-zinc-600" title="Copy link"><Copy size={14} /></button>
                    )}
                    {!t.revoked ? (
                      <button onClick={() => revokeToken(t.id)} data-testid={`revoke-token-${t.id}`} className="p-2 rounded-lg hover:bg-amber-50 text-amber-600" title="Revoke"><Ban size={14} /></button>
                    ) : (
                      <button onClick={() => deleteToken(t.id)} data-testid={`delete-token-${t.id}`} className="p-2 rounded-lg hover:bg-rose-50 text-rose-500" title="Delete"><Trash2 size={14} /></button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">{label}</label>
    {children}
  </div>
);
